import React, { useState } from 'react';
import { ChevronLeft, Heart, Users, Sliders, BarChart3, Plus } from 'lucide-react';
import AddCareProfileModal from './AddCareProfileModal';

const CareProfilesStep = ({ onComplete, onSkip }) => {
  const [showExplanation, setShowExplanation] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [careProfiles, setCareProfiles] = useState([]);

  const handleAddProfile = (profile) => {
    setCareProfiles([...careProfiles, { ...profile, id: Date.now().toString() }]);
    setShowAddModal(false);
  };

  const handleComplete = () => {
    onComplete(careProfiles);
  };

  if (showExplanation) {
    return (
      <div className="max-w-2xl mx-auto px-lg py-xl min-h-screen flex flex-col">
        {/* Back Button */}
        <button
          onClick={onSkip}
          className="mb-xl px-lg py-md bg-white/80 backdrop-blur-sm rounded-full flex items-center gap-sm text-primary font-semibold hover:bg-white transition-all shadow-sm w-fit"
        >
          <ChevronLeft size={20} />
          Back
        </button>

        <div className="flex-1 flex flex-col justify-center">
          {/* Icon */}
          <div className="w-24 h-24 rounded-full bg-[#2D5F4D] flex items-center justify-center mx-auto mb-xl">
            <Heart size={48} className="text-white" />
          </div>

          {/* Title */}
          <h1 className="text-[40px] font-serif text-[#2D5F4D] text-center mb-md" style={{ fontFamily: 'Georgia, serif' }}>
            Caring for Others
          </h1>
          
          <p className="text-body text-[#8B7355] text-center mb-xl max-w-lg mx-auto">
            Track dietary needs and preferences for the people you care about. Perfect for families, caregivers, and anyone managing multiple dietary profiles.
          </p>

          {/* Features */}
          <div className="space-y-lg mb-xl">
            <div className="flex items-start gap-md">
              <div className="w-12 h-12 rounded-full bg-[#2D5F4D]/10 flex items-center justify-center flex-shrink-0">
                <Users size={24} className="text-[#2D5F4D]" />
              </div>
              <div>
                <h3 className="text-callout font-semibold text-[#2D5F4D] mb-xs">Individual Profiles</h3>
                <p className="text-body text-[#8B7355]">Track dietary needs for each person you care for</p>
              </div>
            </div>

            <div className="flex items-start gap-md">
              <div className="w-12 h-12 rounded-full bg-[#2D5F4D]/10 flex items-center justify-center flex-shrink-0">
                <Sliders size={24} className="text-[#2D5F4D]" />
              </div>
              <div>
                <h3 className="text-callout font-semibold text-[#2D5F4D] mb-xs">Universal Filtering</h3>
                <p className="text-body text-[#8B7355]">Filter recipes and products for everyone's needs</p>
              </div>
            </div>

            <div className="flex items-start gap-md">
              <div className="w-12 h-12 rounded-full bg-[#2D5F4D]/10 flex items-center justify-center flex-shrink-0">
                <BarChart3 size={24} className="text-[#2D5F4D]" />
              </div>
              <div>
                <h3 className="text-callout font-semibold text-[#2D5F4D] mb-xs">Nutrition Tracking</h3>
                <p className="text-body text-[#8B7355]">Optional per-person calorie goals and tracking</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-md">
            <button
              onClick={() => setShowExplanation(false)}
              className="w-full py-lg rounded-2xl bg-gradient-to-r from-[#2D5F4D] to-[#3A6E52] text-white font-semibold text-callout hover:shadow-lg transition-all"
            >
              Add Profiles
            </button>
            
            <button
              onClick={onSkip}
              className="w-full py-md text-[#8B7355] font-medium text-callout hover:text-[#2D5F4D] transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Profile Management View
  return (
    <div className="max-w-2xl mx-auto px-lg py-xl min-h-screen flex flex-col">
      {/* Back Button */}
      <button
        onClick={() => setShowExplanation(true)}
        className="mb-xl px-lg py-md bg-white/80 backdrop-blur-sm rounded-full flex items-center gap-sm text-primary font-semibold hover:bg-white transition-all shadow-sm w-fit"
      >
        <ChevronLeft size={20} />
        Back
      </button>

      {/* Header */}
      <div className="mb-xl">
        <h1 className="text-[40px] font-serif text-[#2D5F4D] mb-sm" style={{ fontFamily: 'Georgia, serif' }}>
          Care Profiles
        </h1>
        <p className="text-body text-[#8B7355]">Add family members or people you care for</p>
      </div>

      {/* Profiles List */}
      <div className="flex-1">
        {careProfiles.length === 0 ? (
          <div className="text-center py-xl">
            <Users size={48} className="text-[#2D5F4D]/20 mx-auto mb-md" />
            <p className="text-callout text-[#8B7355]">No profiles added yet</p>
          </div>
        ) : (
          <div className="space-y-sm mb-lg">
            {careProfiles.map((profile) => (
              <div
                key={profile.id}
                className="p-lg rounded-2xl bg-white/80 backdrop-blur-sm border border-[#2D5F4D]/10"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-callout font-semibold text-[#2D5F4D]">{profile.name}</h3>
                    <p className="text-caption text-[#8B7355]">{profile.relationship}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-caption text-[#8B7355]">
                      {profile.allergies?.length || 0} allergies
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Profile Button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="w-full py-lg rounded-2xl border-2 border-dashed border-[#2D5F4D]/30 hover:border-[#2D5F4D] hover:bg-[#2D5F4D]/5 text-[#2D5F4D] font-semibold text-callout transition-all flex items-center justify-center gap-sm"
        >
          <Plus size={20} />
          Add Family Member
        </button>
      </div>

      {/* Bottom Actions */}
      <div className="space-y-md mt-xl">
        <button
          onClick={handleComplete}
          className="w-full py-lg rounded-2xl bg-gradient-to-r from-[#2D5F4D] to-[#3A6E52] text-white font-semibold text-callout hover:shadow-lg transition-all"
        >
          {careProfiles.length > 0 ? 'Complete Setup' : 'Continue Without Profiles'}
        </button>
        
        {careProfiles.length === 0 && (
          <button
            onClick={onSkip}
            className="w-full py-md text-[#8B7355] font-medium text-callout hover:text-[#2D5F4D] transition-colors"
          >
            Skip for now
          </button>
        )}
      </div>

      {/* Add Profile Modal */}
      {showAddModal && (
        <AddCareProfileModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddProfile}
        />
      )}
    </div>
  );
};

export default CareProfilesStep;
